/**
 * BookingModal
 *
 * Wraps the Cal.com inline embed in a Headless UI Dialog so visitors can book
 * a call without leaving couplingwnc.com. Mirrors the modal pattern used by
 * ContactSalesModal.
 *
 * Cal.com handle and event slugs are centralized in src/config/calcom.ts.
 */

import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import Cal, { getCalApi } from '@calcom/embed-react';
import {
  CAL_BRAND_COLOR,
  CAL_EVENT_LABELS,
  CalEventType,
  calLink,
  calUrl,
} from '../config/calcom';
import { analyticsService, ConversionEvent } from '../utils/analytics';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Which Cal.com event to load. Defaults to the longer demo slot. */
  eventType?: CalEventType;
}

const BookingModal: React.FC<BookingModalProps> = ({
  isOpen,
  onClose,
  eventType = 'demo',
}) => {
  const [embedFailed, setEmbedFailed] = useState(false);
  const labels = CAL_EVENT_LABELS[eventType];

  // Fire a Meta `Contact` standard event when the demo modal opens. Schedule
  // only fires on confirmed bookings, which is too sparse to optimize on while
  // the funnel ramps up — Contact gives Meta a per-modal-open signal it can
  // train on, and gives Plausible a `Contact` event that splits "saw page" from
  // "clicked to book."
  useEffect(() => {
    if (!isOpen) return;
    analyticsService.logConversion(ConversionEvent.CONTACT, {
      content_name: 'demo_modal_open',
      cal_event_type: eventType,
    });
  }, [isOpen, eventType]);

  // Configure the embed once it's mounted. Cal.com's embed API is namespaced —
  // we use a dedicated namespace per event type so settings don't clash if
  // multiple modals get instantiated in the same session.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const cal = await getCalApi({ namespace: `booking-${eventType}` });
        if (cancelled) return;
        cal('ui', {
          theme: 'light',
          cssVarsPerTheme: {
            light: { 'cal-brand': CAL_BRAND_COLOR },
            dark: { 'cal-brand': CAL_BRAND_COLOR },
          },
          hideEventTypeDetails: false,
          layout: 'month_view',
        });
        // Fire Meta Schedule conversion when Cal.com confirms the booking.
        // Cal exposes attendee email/name in the event detail, which we pass
        // through for CAPI matching (hashed server-side).
        cal('on', {
          action: 'bookingSuccessful',
          callback: (e: unknown) => {
            try {
              const detail = (e as { detail?: { data?: { booking?: unknown; eventType?: { title?: string; length?: number } } } }).detail;
              const booking = detail?.data?.booking as
                | { attendees?: Array<{ email?: string; name?: string }>; uid?: string }
                | undefined;
              const attendee = booking?.attendees?.[0];
              analyticsService.logConversion(
                ConversionEvent.SCHEDULE,
                {
                  content_name: detail?.data?.eventType?.title ?? `cal_${eventType}`,
                  cal_event_type: eventType,
                  cal_booking_uid: booking?.uid,
                  duration_min: detail?.data?.eventType?.length,
                },
                {
                  email: attendee?.email,
                }
              );
            } catch (err) {
              console.warn('[Analytics] Schedule conversion log failed', err);
            }
          },
        });
      } catch (err) {
        // Embed script blocked (e.g., privacy extensions) — fall back to link.
        if (!cancelled) setEmbedFailed(true);
        // eslint-disable-next-line no-console
        console.warn('Cal.com embed failed to initialize:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, eventType]);

  // Reset the failure state when the modal closes so a retry is possible.
  useEffect(() => {
    if (!isOpen) setEmbedFailed(false);
  }, [isOpen]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
                  <div className="text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold leading-6 text-neutral-900"
                    >
                      {labels.long}
                    </Dialog.Title>
                    <p className="text-sm text-neutral-500 mt-0.5">
                      {labels.description}
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label="Close booking dialog"
                    className="rounded-full p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Embed area */}
                <div className="relative bg-neutral-50" style={{ minHeight: 'min(70vh, 640px)' }}>
                  {embedFailed ? (
                    <div className="flex flex-col items-center justify-center text-center p-12 gap-4">
                      <p className="text-neutral-700">
                        We couldn't load the booking widget here — your browser
                        or an extension may have blocked it.
                      </p>
                      <a
                        href={calUrl(eventType)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-brand-primary rounded-full hover:bg-brand-primary-dark transition-colors"
                      >
                        Open booking page in new tab
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      </a>
                    </div>
                  ) : (
                    <Cal
                      key={eventType}
                      namespace={`booking-${eventType}`}
                      calLink={calLink(eventType)}
                      style={{ width: '100%', height: '100%', minHeight: 'min(70vh, 640px)' }}
                      config={{ layout: 'month_view' }}
                    />
                  )}
                </div>

                {/* Footer fallback link — always visible so users have an exit even on flaky embeds */}
                <div className="border-t border-neutral-200 px-6 py-3 text-center">
                  <p className="text-xs text-neutral-500">
                    Trouble booking?{' '}
                    <a
                      href={calUrl(eventType)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-primary hover:underline font-medium"
                    >
                      Open in new tab
                    </a>{' '}
                    or email{' '}
                    <a
                      href="mailto:hello@cplng.org"
                      className="text-brand-primary hover:underline font-medium"
                    >
                      hello@cplng.org
                    </a>
                    .
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BookingModal;
